/**
 * Google登录按钮组件
 * 使用服务器端OAuth流程
 */

import React, { useState } from 'react';

/**
 * Google登录按钮组件属性
 */
interface GoogleLoginButtonProps {
  /**
   * 登录成功回调
   */
  onSuccess?: () => void;
  
  /**
   * 登录失败回调
   */
  onError?: (error: string) => void;
  
  /**
   * 按钮文本
   */
  text?: string;
  
  /**
   * 登录后跳转的returnUrl
   */
  returnUrl?: string;
}

/**
 * Google登录按钮组件
 * 使用服务器端OAuth流程
 */
const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({
  onSuccess,
  onError,
  text = 'Google登录',
  returnUrl
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      
      // 拼接returnUrl参数  
      const AUTH_API_BASE = process.env.REACT_APP_AUTH_API_BASE || 'http://localhost:9001';
      let url = `${AUTH_API_BASE}/api/v1/auth/oauth/google`;
      if (returnUrl) {
        url += `?returnUrl=${encodeURIComponent(returnUrl)}`;
      }
      
      // 调用后端获取Google OAuth URL
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.auth_url) {
        // 重定向到Google OAuth页面
        window.location.href = data.auth_url;
      } else {
        throw new Error('未获取到Google OAuth URL');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Google登录失败';
      onError?.(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleGoogleLogin}
      disabled={isLoading}
      style={{
        width: '100%',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 12px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        backgroundColor: 'white',
        color: '#374151',
        fontSize: '14px',
        fontWeight: '500',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        opacity: isLoading ? 0.5 : 1,
        transition: 'background-color 0.2s',
        gap: '8px'
      }}
      onMouseEnter={(e) => {
        if (!isLoading) {
          e.currentTarget.style.backgroundColor = '#f9fafb';
        }
      }}
      onMouseLeave={(e) => {
        if (!isLoading) {
          e.currentTarget.style.backgroundColor = 'white';
        }
      }}
    >
      {isLoading ? (
        <>
          <div style={{
            width: '14px',
            height: '14px',
            border: '2px solid #d1d5db',
            borderTop: '2px solid #2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span>登录中...</span>
        </>
      ) : (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
        </>
      )}
    </button>
  );
};

export default GoogleLoginButton; 